import { Outlet, Link, useLocation } from 'react-router-dom';
import { Settings } from 'lucide-react';
import { SETTINGS_GROUPS } from '../../config/navigation';
import { itemsOn } from '../../config/navigationModules';
import { useMemberScope } from '../../hooks/useMemberScope';
import { useCanOpen } from '../../hooks/useMyAccess';
import { useSwitchedOffModules } from '../../hooks/useSwitchedOffModules';

export function SettingsLayout() {
  const location = useLocation();

  // Show index page when on exact /admin/settings or /admin/settings/
  const isIndex = location.pathname === '/admin/settings' || location.pathname === '/admin/settings/';

  // Find active item for breadcrumb
  const activeItem = SETTINGS_GROUPS
    .flatMap(g => g.items.map(item => ({ ...item, group: g.label })))
    .find(item => location.pathname === item.href);

  if (isIndex) {
    return <SettingsIndex />;
  }

  return (
    <div className="max-w-5xl mx-auto">
      {/* Breadcrumb header */}
      <div className="flex items-center space-x-2 mb-6 text-sm">
        <Link
          to="/admin/settings"
          className="flex items-center text-neutral-500 hover:text-neutral-900 transition-colors"
        >
          <Settings className="h-4 w-4 mr-1.5" />
          Paramètres
        </Link>
        {activeItem && (
          <>
            <span className="text-neutral-300">/</span>
            <span className="text-neutral-400">{activeItem.group}</span>
            <span className="text-neutral-300">/</span>
            <span className="text-neutral-900 font-medium">{activeItem.name}</span>
          </>
        )}
      </div>

      <Outlet />
    </div>
  );
}

function SettingsIndex() {
  // A module switched off takes its settings away (PLAN-GENERICITE § 3.7).
  const off = useSwitchedOffModules();
  const scope = useMemberScope();
  const canOpen = useCanOpen();
  const groups = SETTINGS_GROUPS.map((group) => ({ ...group, items: itemsOn(group.items, off, scope, canOpen) })).filter((group) => group.items.length > 0);
  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-neutral-900">Paramètres</h1>
        <p className="text-sm text-neutral-500 mt-1">Configurez votre application et vos intégrations</p>
      </div>

      <div className="space-y-8">
        {groups.map((group) => (
          <div key={group.label}>
            <h2 className="text-xs font-semibold uppercase tracking-wider text-neutral-400 mb-3 px-1">
              {group.label}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {group.items.map((item) => {
                const ItemIcon = item.icon;
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    className="group flex items-center space-x-3 p-4 bg-white rounded-xl border border-neutral-200 hover:border-[#0066FF]/30 hover:shadow-md hover:shadow-blue-500/5 transition-all duration-200"
                  >
                    <div className="w-10 h-10 rounded-lg bg-neutral-100 group-hover:bg-blue-50 flex items-center justify-center transition-colors flex-shrink-0">
                      <ItemIcon className="h-5 w-5 text-neutral-500 group-hover:text-[#0066FF] transition-colors" />
                    </div>
                    <span className="text-sm font-medium text-neutral-700 group-hover:text-neutral-900 transition-colors">
                      {item.name}
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
